/**
 * Picture-in-Picture (PiP) ユーティリティ
 *
 * 購入導線で別タブへ遷移する直前に PiP を開始し、視聴継続を可能にする。
 * - Chromium系: HTMLVideoElement.requestPictureInPicture()
 * - Safari系: video.webkitSetPresentationMode("picture-in-picture")
 *
 * 注意: PiP API はユーザージェスチャー内で呼ぶ必要があるため、
 *       クリックハンドラ内で同期的に呼び出すこと（await しない）。
 *
 * iOS Safari の制約:
 *   iOS Safari では1回のユーザージェスチャーで許可される特権的アクションは1つだけ。
 *   PiP を呼ぶと window.open がブロックされるため、iOS では商品選択時に PiP を開始し、
 *   購入ボタンでは window.open のみを行う。
 */

import { isMobileOrTablet } from "@/utils/deviceDetect";

// Safari 用の拡張型定義
interface WebKitHTMLVideoElement extends HTMLVideoElement {
  webkitSetPresentationMode?: (mode: "inline" | "fullscreen" | "picture-in-picture") => void;
  webkitPresentationMode?: "inline" | "fullscreen" | "picture-in-picture";
}

// Document の PiP 拡張
// TypeScriptの標準ライブラリで既に定義されているプロパティを除外してから再定義
interface DocumentWithPiP extends Omit<Document, 'pictureInPictureElement' | 'pictureInPictureEnabled'> {
  pictureInPictureElement?: Element | null;
  pictureInPictureEnabled?: boolean;
}

/**
 * iOS/iPadOS デバイスかどうかを判定
 * iPadOS 13+ は User Agent が "Macintosh" になるため、タッチ対応も確認する
 */
export function isIOS(): boolean {
  if (typeof navigator === "undefined") return false;
  
  // iPhone/iPod/古いiPad
  if (/iPhone|iPod/.test(navigator.userAgent)) {
    return true;
  }
  
  // iPad (iPadOS 13未満)
  if (/iPad/.test(navigator.userAgent)) {
    return true;
  }
  
  // iPadOS 13+ (User Agent が Macintosh になる)
  // タッチ対応かつ Macintosh の場合は iPad と判定
  if (/Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1) {
    return true;
  }
  
  return false;
}

/**
 * URL を新しいタブで開く。
 * iOS Safari では window.open が同じタブで開くことがあるため、
 * <a> タグを使って確実に新しいタブで開く。
 *
 * @param url - 開く URL
 */
export function openInNewTab(url: string): void {
  if (isIOS()) {
    // iOS Safari: <a> タグを動的に作成してクリックすることで確実に新しいタブで開く
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.target = "_blank";
    anchor.rel = "noopener noreferrer";
    anchor.style.display = "none";
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  } else {
    window.open(url, "_blank");
  }
}

/**
 * 指定プレイヤーの動画を PiP モードへ移行する（可能な場合）。
 * 失敗・未対応の場合は何もせず、購入導線をブロックしない。
 * ※スマホ/タブレットのみで動作
 *
 * @param playerId - プレイヤーの ID（DOM 上で `player-${playerId}` を探す）
 */
export function tryEnterPiP(playerId: string): void {
  try {
    // スマホ/タブレット以外（PC）では何もしない
    if (!isMobileOrTablet()) {
      return;
    }

    // data属性で直接video要素を探す
    // フルスクリーン時はreact-modalがbody直下にレンダリングするため、
    // player-${playerId}内からの検索では見つからない場合がある
    const video = document.querySelector(
      `video[data-foove-player-id="${playerId}"]`
    ) as WebKitHTMLVideoElement | null;

    if (!video) {
      return;
    }

    const doc = document as DocumentWithPiP;

    // すでに PiP 中なら何もしない（Chromium系）
    if (doc.pictureInPictureElement) {
      return;
    }

    // Safari: webkitPresentationMode で既に PiP 中かチェック
    if (video.webkitPresentationMode === "picture-in-picture") {
      return;
    }

    // Chromium系 (Chrome, Edge, etc.)
    if (typeof video.requestPictureInPicture === "function") {
      // awaitしない: ユーザージェスチャーのスタックを維持するため
      video.requestPictureInPicture().catch(() => {
        // PiP 拒否/失敗は握りつぶす（購入導線を優先）
      });
      return;
    }

    // Safari系 (iOS/macOS)
    if (typeof video.webkitSetPresentationMode === "function") {
      video.webkitSetPresentationMode("picture-in-picture");
      return;
    }
  } catch {
    // PiP 不可/拒否は無視して購入導線を優先
  }
}

/**
 * PiP モードを解除する（可能な場合）。
 * ※スマホ/タブレットのみで動作
 */
export function tryExitPiP(): void {
  try {
    // スマホ/タブレット以外（PC）では何もしない
    if (!isMobileOrTablet()) {
      return;
    }
    // PiP 中なら解除
    if (document.pictureInPictureElement) {
      document.exitPictureInPicture();
      return;
    }
  } catch {
    // PiP 解除不可/拒否は無視
  }
}

